import styled, { css, keyframes } from 'styled-components';
import { exFont } from '../typography';

// Shared Exams primitives. Colours come only from ExamsScope's tokens.

const TONES = {
  ok: css`
    color: var(--ex-ok-ink);
    background: var(--ex-ok-bg);
  `,
  warn: css`
    color: var(--ex-warn-ink);
    background: var(--ex-warn-bg);
  `,
  bad: css`
    color: var(--ex-bad-ink);
    background: var(--ex-bad-bg);
  `,
  info: css`
    color: var(--ex-info-ink);
    background: var(--ex-info-bg);
  `,
  purple: css`
    color: var(--ex-purple);
    background: var(--ex-purple-100);
  `,
  neutral: css`
    color: var(--ex-ink-2);
    background: var(--ex-surface-2);
  `,
};

export const toneCss = (tone) => TONES[tone] || TONES.neutral;

export const BadgeRoot = styled.span`
  ${exFont(600, 'xs')}
  ${({ $tone }) => toneCss($tone)}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${({ $square }) =>
    $square ? '6px' : 'var(--ex-radius-pill)'};
  white-space: nowrap;
  line-height: 1.4;
`;

export const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

const PILL_VARIANTS = {
  outline: css`
    color: var(--ex-purple);
    background: var(--white);
    border: 1px solid var(--ex-purple);
  `,
  primary: css`
    color: var(--white);
    background: var(--ex-purple);
    border: 1px solid var(--ex-purple);
    &:hover:not(:disabled) {
      box-shadow: var(--ex-shadow-accent);
    }
  `,
  secondary: css`
    color: var(--ex-purple);
    background: var(--ex-purple-100);
    border: 1px solid var(--ex-purple-200);
    &:hover:not(:disabled) {
      background: var(--ex-purple-150);
    }
  `,
  ghost: css`
    color: var(--ex-ink-strong);
    background: transparent;
    border: 1px solid var(--ex-line-strong);
    &:hover:not(:disabled) {
      background: var(--ex-surface-2);
    }
  `,
  danger: css`
    color: var(--white);
    background: var(--ex-bad-ink);
    border: 1px solid var(--ex-bad-ink);
  `,
};

export const PillButton = styled.button`
  ${({ $size }) => exFont(500, $size === 'small' ? 's' : 'm')}
  ${({ $variant }) => PILL_VARIANTS[$variant] || PILL_VARIANTS.primary}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: ${({ $size }) => ($size === 'small' ? '4px 12px' : '8px 16px')};
  border-radius: var(--ex-radius-pill);
  cursor: pointer;
  transition: background 0.1s, box-shadow 0.1s;
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SegmentedRoot = styled.div`
  display: inline-flex;
  padding: 4px;
  gap: 4px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-surface-2);
`;

export const SegmentedOption = styled.button`
  ${exFont(500, 's')}
  padding: 4px 12px;
  border: none;
  border-radius: var(--ex-radius-pill);
  cursor: pointer;
  color: ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-ink-2)')};
  background: ${({ $active }) => ($active ? 'var(--white)' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? 'var(--ex-shadow-1)' : 'none')};
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ $z }) => $z || 1400};
  background: var(--ex-scrim);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DialogCard = styled.div`
  width: min(480px, calc(100vw - 32px));
  background: var(--white);
  border-radius: 16px;
  box-shadow: var(--ex-shadow-modal);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* focused as a container by useModalFocus; its controls keep their rings */
  &:focus {
    outline: none;
  }
`;

export const DialogTitle = styled.h2`
  ${exFont(600, 'xl')}
  margin: 0;
  color: var(--ex-ink);
`;

export const DialogBody = styled.div`
  ${exFont(400, 'm')}
  color: var(--ex-ink-2);
  line-height: 1.5;
`;

export const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

export const OverlayBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ $z }) => ($z || 1300) - 1};
  background: rgba(37, 34, 42, 0.55);
`;

export const OverlayRoot = styled.div`
  position: fixed;
  inset: ${({ $inset }) =>
    $inset ? '24px max(24px, calc((100vw - 1080px) / 2))' : '0'};
  z-index: ${({ $z }) => $z || 1300};
  border-radius: ${({ $inset }) => ($inset ? '16px' : '0')};
  max-height: ${({ $inset }) => ($inset ? '760px' : 'none')};
  margin: ${({ $inset }) => ($inset ? 'auto' : '0')};
  background: var(--white);
  display: flex;
  flex-direction: column;
  overflow: auto;
  line-height: normal;
  /* focused as a container by useModalFocus; its controls keep their rings */
  &:focus {
    outline: none;
  }
`;

export const OverlayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 22px;
  border-bottom: 1px solid var(--ex-line);
  > button {
    width: 20px;
    height: 20px;
    padding: 0;
  }
`;

export const OverlayHeading = styled.div`
  flex: 1;
`;

export const OverlaySubtitle = styled.p`
  ${exFont(400, 's')}
  margin: 2px 0 0;
  color: var(--ex-ink-2);
`;

export const OverlayTitle = styled.h2`
  ${exFont(700, 19)}
  margin: 0;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ex-ink-strong);
  cursor: pointer;
  &:hover {
    background: var(--ex-surface-2);
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const ToastCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: ${fadeIn} 0.15s ease-out;
`;

export const ToastTitle = styled.p`
  ${exFont(600, 'm')}
  margin: 0;
  color: var(--ex-ink);
`;

export const ToastBody = styled.p`
  ${exFont(400, 's')}
  margin: 0;
  color: var(--ex-ink-2);
  line-height: 1.45;
`;

export const InlineErrorRoot = styled.div`
  ${exFont(400, 's')}
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-radius: var(--ex-radius-item);
  color: var(--ex-bad-ink);
  background: var(--ex-bad-bg);
`;

export const InlineErrorAction = styled.button`
  ${exFont(600, 's')}
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;
